import { useCallback, useState } from "react";
import {
  listOrganizationsReq,
  createOrganizationReq,
  updateOrganizationReq,
  listOrganizationTeamsReq,
  moveTeamReq,
} from "services/api";
import { ORGANIZATIONS_MESSAGES } from "./organizations.constants";

const parseErrorMessage = (err, fallbackMessage) => {
  const data = err?.response?.data;
  if (typeof data === "string" && data) return data;
  return data?.message || data?.errors?.[0]?.msg || fallbackMessage;
};

function useOrganizations(adminId) {
  const [organizations, setOrganizations] = useState([]);
  const [teams, setTeams] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!adminId) return;
    setIsLoading(true);
    try {
      const [organizationsRes, teamsRes] = await Promise.all([
        listOrganizationsReq({ admin_id: adminId }),
        listOrganizationTeamsReq({ admin_id: adminId }),
      ]);
      setOrganizations(Array.isArray(organizationsRes.data) ? organizationsRes.data : []);
      setTeams(Array.isArray(teamsRes.data) ? teamsRes.data : []);
    } catch (err) {
      setErrorMessage(parseErrorMessage(err, "Failed to load organizations."));
    }
    setIsLoading(false);
  }, [adminId]);

  // Runs a change, reports the result, and reloads the lists. Returns true on success.
  const run = async (request, successText, failureText) => {
    try {
      setErrorMessage(null);
      setSuccessMessage(null);
      const res = await request();
      setSuccessMessage(typeof successText === "function" ? successText(res) : successText);
      await fetchAll();
      return true;
    } catch (err) {
      setErrorMessage(parseErrorMessage(err, failureText));
      return false;
    }
  };

  const createOrganization = (body) =>
    run(
      () => createOrganizationReq({ admin_id: adminId, body }),
      ORGANIZATIONS_MESSAGES.created,
      "Failed to create organization."
    );

  const updateOrganization = (id, body) =>
    run(
      () => updateOrganizationReq({ admin_id: adminId, id, body }),
      ORGANIZATIONS_MESSAGES.updated,
      "Failed to update organization."
    );

  const updateFeatures = (id, features) =>
    run(
      () => updateOrganizationReq({ admin_id: adminId, id, body: { features } }),
      ORGANIZATIONS_MESSAGES.featuresUpdated,
      "Failed to update features."
    );

  const moveTeam = (teamId, organizationId) =>
    run(
      () =>
        moveTeamReq({
          admin_id: adminId,
          team_id: teamId,
          body: { organization_id: organizationId },
        }),
      (res) =>
        res.data.removed_admin_links
          ? `${ORGANIZATIONS_MESSAGES.moved} ${res.data.removed_admin_links} admin link(s) to it were removed.`
          : ORGANIZATIONS_MESSAGES.moved,
      "Failed to move workspace."
    );

  return {
    organizations,
    teams,
    isLoading,
    errorMessage,
    successMessage,
    fetchAll,
    createOrganization,
    updateOrganization,
    updateFeatures,
    moveTeam,
    showError: setErrorMessage,
    clearError: () => setErrorMessage(null),
    clearSuccess: () => setSuccessMessage(null),
  };
}

export default useOrganizations;
